import type { Rng } from '../rng/seededRandom';
import type { BattleAiMemory } from './ai';
import {
  bestDirection,
  coverSpot,
  distanceXZ,
  flip,
  followPath,
  footholdAhead,
  normalizeDir,
  openness,
  standable,
  type Dir2,
  type TileCost,
} from './aiNavigation';
import type { View } from './aiPerception';
import type { SkillProfile } from './aiSkill';
import { contactReach } from './aiContact';
import { centre, horizontalDistance, sightBlocked } from './combat';
import type { TileMap } from './tilemap';
import type { BattleState, Fighter, Vec3 } from './types';

/** What the enemy shows: its size, weapon and speed are public; its cooldown and stun are not. */
export type KnownEnemy = Pick<
  Fighter,
  'halfWidth' | 'height' | 'attackType' | 'range' | 'speed' | 'maxHp' | 'attackInterval'
>;

/** Everything a behaviour may look at this tick. */
export interface Situation {
  map: TileMap;
  state: BattleState;
  me: Fighter;
  enemy: KnownEnemy;
  view: View;
  memory: BattleAiMemory;
  profile: SkillProfile;
  rng: Rng;
  dt: number;
  ranged: boolean;
  enemyRanged: boolean;
  /** Badly hurt and behind: trade less, keep range, break line of sight. */
  careful: boolean;
}

export interface Plan {
  /** World-space direction to move in, or null to stand. */
  move: Dir2 | null;
  /** Face the enemy (attack, strafe, backpedal) or the way of travel (run flat out). */
  face: 'enemy' | 'move';
  /** Whether attacking is on the table; the act step still checks aim and cadence. */
  attack: boolean;
  /** 0…1 of the available speed. */
  throttle?: number;
}

/** Tiles in the enemy's sight cost this much extra when a careful fighter plans its approach. */
const EXPOSED_COST = 2.5;
/** Within this distance and in sight, a fighter walks straight at the enemy instead of pathing. */
const DIRECT_APPROACH = 5;

/** Priorities: not getting hit, not burning, then the weapon's own game plan. */
export function choose(s: Situation): Plan {
  return (
    dodge(s) ??
    stayInSafeZone(s) ??
    (s.ranged ? (takeCover(s) ?? kite(s) ?? regainSight(s) ?? holdRange(s)) : closeIn(s))
  );
}

/** Sidesteps a shot that is about to hit, on the side that takes the fighter off its course. */
function dodge(s: Situation): Plan | null {
  const { memory, view, state, map, me } = s;
  if (memory.dodge && state.time < memory.dodge.until)
    return { move: memory.dodge.dir, face: 'enemy', attack: true };
  memory.dodge = null;
  let threat = null;
  for (const t of view.threats) if (!threat || t.timeToImpact < threat.timeToImpact) threat = t;
  if (!threat) return null;
  const preferred = threat.sidestep ?? strafeSide(s);
  if (!preferred) return null;
  const dir = footholdAhead(map, me, preferred, 0.7)
    ? preferred
    : footholdAhead(map, me, flip(preferred), 0.7)
      ? flip(preferred)
      : null;
  if (!dir) return null;
  memory.dodge = { dir, until: state.time + threat.timeToImpact + 0.1 };
  return { move: dir, face: 'enemy', attack: true };
}

/** Once the arena burns, whoever is near the edge of the safe zone heads for its centre. */
function stayInSafeZone(s: Situation): Plan | null {
  const { state, me, view, map, memory, dt } = s;
  const centre = state.safeCentre;
  if (!state.suddenDeath) return null;
  if (distanceXZ(me.pos, centre) < state.safeRadius - 1.5) return null;
  const move = followPath(map, me, { x: centre.x, y: me.pos.y, z: centre.z }, memory, dt);
  if (!move) return null;
  return { move, face: view.los && view.dist < 9 ? 'enemy' : 'move', attack: true };
}

/** Shooters duck behind something while their weapon is not ready and the enemy's may be. */
function takeCover(s: Situation): Plan | null {
  const { me, view, memory, profile, map, state, dt } = s;
  const radius = profile.coverRadius * (s.careful ? 2 : 1);
  if (!s.enemyRanged || radius < 1) return null;
  const shotSoon = me.cooldown <= profile.reaction + 0.1;
  const wanted = !shotSoon && (s.careful || view.enemyCanShootIn < me.cooldown);
  if (!wanted) {
    memory.cover = null;
    return null;
  }
  if (!memory.cover || state.time >= memory.cover.until) {
    const spot = coverSpot(map, me, view.enemyEye, radius, (at) => !outsideSafeZone(s, at));
    memory.cover = { spot, until: state.time + 0.5 };
  }
  const spot = memory.cover.spot;
  if (!spot) return null;
  if (distanceXZ(spot, me.pos) < 0.3) return { move: null, face: 'enemy', attack: false };
  return { move: followPath(map, me, spot, memory, dt), face: 'enemy', attack: false };
}

/**
 * Shooters keep a shooter's distance. Against something that must come close they run for open
 * ground and turn to shoot when the shot is ready; against another
 * shooter they back off while firing, since a turned back is a free hit.
 */
function kite(s: Situation): Plan | null {
  const { me, view, map, memory } = s;
  if (view.dist >= preferredRange(s) - 0.5) return null;
  const run = bestDirection(map, me, (at, dir) => {
    const keepGoing = memory.moveDir ? dir.x * memory.moveDir.x + dir.z * memory.moveDir.z : 0;
    return (
      distanceXZ(at, view.enemyPos) +
      1.5 * openness(map, me, dir) +
      0.4 * keepGoing -
      safePenalty(s, at)
    );
  });
  if (!run) return null;
  // Never stop retaliating just because a pursuer has closed the gap. A slower
  // shooter cannot escape that state and otherwise flees until dead without firing.
  const standAndShoot = view.los && me.cooldown <= 0.15;
  const faceEnemy = s.enemyRanged || standAndShoot;
  return { move: run, face: faceEnemy ? 'enemy' : 'move', attack: faceEnemy };
}

/**
 * A shooter that has lost sight of something that must come close does not walk in after it:
 * it steps to where it can see again while keeping its distance, and lets the enemy come.
 */
function regainSight(s: Situation): Plan | null {
  const { me, view, map } = s;
  const far = preferredRange(s);
  if (view.los || s.enemyRanged || view.dist > far) return null;
  const target = view.enemyCentre;
  const move = bestDirection(map, me, (at, dir) => {
    const sees = !sightBlocked(map, { x: at.x, y: at.y + me.height * 0.9, z: at.z }, target);
    return (
      (sees ? 5 : 0) +
      Math.min(distanceXZ(at, view.enemyPos), far) +
      openness(map, me, dir) -
      safePenalty(s, at)
    );
  });
  return move ? { move, face: 'enemy', attack: false } : null;
}

/** At a comfortable distance: strafe and shoot; without a line of sight, or too far away, close up. */
function holdRange(s: Situation): Plan {
  const { me, view, map, memory, dt } = s;
  if (!view.los || view.dist > preferredRange(s) + 2.5) {
    return {
      move: followPath(map, me, view.enemyPos, memory, dt),
      face: view.los ? 'enemy' : 'move',
      attack: view.los,
    };
  }
  return { move: strafeSide(s), face: 'enemy', attack: true, throttle: 0.9 };
}

/**
 * Melee and the like: get within reach (along a path that hugs cover when hurt and facing a
 * shooter, weaving so a led shot misses) and strike from a striking distance rather than from
 * inside the enemy, where the facing is guesswork.
 */
function closeIn(s: Situation): Plan {
  const { me, view, map, memory, profile, dt } = s;
  const toEnemy = normalizeDir(view.enemyPos.x - me.pos.x, view.enemyPos.z - me.pos.z);
  if (view.los && inReach(s)) {
    if (toEnemy && view.dist < me.range * 0.5)
      return { move: flip(toEnemy), face: 'enemy', attack: true, throttle: 0.6 };
    return { move: strafeSide(s), face: 'enemy', attack: true, throttle: 0.85 };
  }
  const cost = s.careful && s.enemyRanged ? exposedCost(s) : undefined;
  let move =
    view.los && view.dist < DIRECT_APPROACH && toEnemy && footholdAhead(map, me, toEnemy, 1)
      ? toEnemy
      : followPath(map, me, chaseGoal(s), memory, dt, cost);
  if (!move) return { move: null, face: 'enemy', attack: false };
  if (s.enemyRanged && view.dist > 3 && profile.weave > 0) {
    const w = profile.weave * memory.strafeDir;
    move = normalizeDir(move.x + move.z * w, move.z - move.x * w) ?? move;
  }
  const headOn = !toEnemy || move.x * toEnemy.x + move.z * toEnemy.z > 0.5;
  return { move, face: headOn || view.dist < 3 ? 'enemy' : 'move', attack: false };
}

function inReach(s: Situation): boolean {
  const { me, enemy, view, profile } = s;
  if (me.attackType === 'aura' || me.attackType === 'burst' || me.attackType === 'burn')
    return (
      horizontalDistance(centre(me), view.enemyCentre) <=
      contactReach(me.range, profile.reaction, profile.anticipation, view.enemyVel) - 0.2
    );
  return view.dist <= me.range + enemy.halfWidth - 0.15;
}

/** Where to head for: a little ahead of a moving enemy, when that spot is standable. */
function chaseGoal(s: Situation): Vec3 {
  const { view, me, profile, map } = s;
  const t = Math.min(0.3, view.dist / Math.max(1, me.speed)) * profile.lead;
  if (t <= 0) return view.enemyPos;
  const ahead = {
    x: view.enemyPos.x + view.enemyVel.x * t,
    y: view.enemyPos.y,
    z: view.enemyPos.z + view.enemyVel.z * t,
  };
  return standable(map, ahead, me.movement) ? ahead : view.enemyPos;
}

/** Path cost that makes tiles the enemy can see expensive; cached per plan. */
function exposedCost(s: Situation): TileCost {
  const { map, view, me } = s;
  const eye = view.enemyEye;
  const cache = new Map<number, number>();
  return (x, y) => {
    const key = map.index(x, y);
    const known = cache.get(key);
    if (known !== undefined) return known;
    const at = { x: x + 0.5, y: me.pos.y + me.height / 2, z: y + 0.5 };
    const cost = sightBlocked(map, eye, at) ? 0 : EXPOSED_COST;
    cache.set(key, cost);
    return cost;
  };
}

/** Sideways to the enemy in the current strafe direction, flipping it when a wall is in the way. */
function strafeSide(s: Situation): Dir2 | null {
  const { me, view, map, memory } = s;
  const toEnemy = normalizeDir(view.enemyPos.x - me.pos.x, view.enemyPos.z - me.pos.z) ?? {
    x: 0,
    z: 1,
  };
  let side = { x: toEnemy.z * memory.strafeDir, z: -toEnemy.x * memory.strafeDir };
  if (footholdAhead(map, me, side, 0.8)) return side;
  memory.strafeDir = memory.strafeDir === 1 ? -1 : 1;
  side = flip(side);
  return footholdAhead(map, me, side, 0.8) ? side : null;
}

/** The distance a shooter likes, wandering a little at low accuracy and longer when hurt. */
function preferredRange(s: Situation): number {
  return s.profile.rangedDistance * (1 + s.memory.rangeWobble) + (s.careful ? 2 : 0);
}

function outsideSafeZone(s: Situation, at: Dir2): boolean {
  return s.state.suddenDeath && distanceXZ(at, s.state.safeCentre) > s.state.safeRadius - 1;
}

function safePenalty(s: Situation, at: Dir2): number {
  return outsideSafeZone(s, at) ? 10 : 0;
}
